import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Box, DraftingCompass, Download, Files, Lock, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table';
import { useDownloadEngFile, useEngLinksForItem, type EngFile } from '@/features/engineering/hooks';
import {
  ENG_FILE_CATEGORY_LABELS,
  ENG_ITEM_RELATIONSHIP_META,
  formatBytes,
  isViewable3D,
  type EngFileCategory,
  type EngItemRelationship,
} from '@/features/engineering/model';
import { formatError } from '@/lib/errors';
import { formatDateOnly, formatRelative } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import { EngStatusBadge, FileIcon } from './EngParts';
import { ModelViewerDialog, type ModelViewerTarget } from './ModelViewerDialog';

function relationshipMeta(value: string) {
  return ENG_ITEM_RELATIONSHIP_META[value as EngItemRelationship] ?? { label: value, hint: '', variant: 'muted' as const };
}

/**
 * Engineering tab on the catalog item page: the projects that design
 * or use this item (one click to their file drive) and any files that
 * were pinned to the item directly, with 3D preview and download.
 */
export function ItemEngineeringTab({ itemId }: { itemId: string }) {
  const { data, isLoading, error } = useEngLinksForItem(itemId);
  const download = useDownloadEngFile();
  const [viewer, setViewer] = useState<ModelViewerTarget | null>(null);

  const projects = useMemo(
    () => (data?.projects ?? []).filter((l) => l.project),
    [data?.projects],
  );
  const files = data?.files ?? [];

  const handleDownload = async (file: EngFile) => {
    try {
      await download.mutateAsync({ file, checkOut: false });
    } catch (err) {
      toast.error(formatError(err, 'Could not download the file.'));
    }
  };

  if (error) {
    return <EmptyState icon={DraftingCompass} title="Could not load engineering links" detail={formatError(error)} />;
  }
  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-20 w-full" />
        ))}
      </div>
    );
  }
  if (projects.length === 0 && files.length === 0) {
    return (
      <EmptyState
        icon={DraftingCompass}
        title="No engineering project is linked to this item yet"
        detail="Link it from an engineering project’s Overview tab (Production items card) and its files will show up here."
        action={
          <Button variant="outline" asChild>
            <Link to="/engineering">
              Open engineering
              <ArrowUpRight className="ml-1.5 h-4 w-4" />
            </Link>
          </Button>
        }
      />
    );
  }

  return (
    <div className="space-y-6">
      {projects.length > 0 && (
        <section className="space-y-3">
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Engineering projects</h3>
          <div className="grid gap-3 lg:grid-cols-2">
            {projects.map((link) => {
              const p = link.project!;
              const rel = relationshipMeta(link.relationship);
              return (
                <Card key={p.id} className={cn(p.archived_at && 'opacity-70')}>
                  <CardContent className="flex flex-col gap-3 pt-5">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-mono text-xs text-muted-foreground">{p.code}</p>
                        <Link to={`/engineering/${p.id}`} className="block truncate text-base font-medium text-foreground hover:underline">
                          {p.name}
                        </Link>
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Badge variant={rel.variant}>{rel.label}</Badge>
                        <EngStatusBadge status={p.status} />
                      </div>
                    </div>
                    {link.note && <p className="text-sm text-muted-foreground">{link.note}</p>}
                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      {p.design_locked_at ? (
                        <span className="inline-flex items-center gap-1">
                          <Lock className="h-3 w-3" />
                          Design locked {formatDateOnly(p.design_locked_at.slice(0, 10))}
                        </span>
                      ) : (
                        <span>Updated {formatRelative(p.updated_at)}</span>
                      )}
                      {p.archived_at && <span>· Archived</span>}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" asChild>
                        <Link to={`/engineering/${p.id}?tab=files`}>
                          <Files className="h-4 w-4" />
                          Engineering files
                        </Link>
                      </Button>
                      <Button size="sm" variant="outline" asChild>
                        <Link to={`/engineering/${p.id}?tab=prints`}>
                          <Printer className="h-4 w-4" />
                          3D prints
                        </Link>
                      </Button>
                      <Button size="sm" variant="ghost" asChild>
                        <Link to={`/engineering/${p.id}`}>
                          Open project
                          <ArrowUpRight className="ml-1 h-4 w-4" />
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>
      )}

      {files.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Files pinned to this item</CardTitle>
            <p className="text-xs text-muted-foreground">Latest version of each file. Open a project for the full history.</p>
          </CardHeader>
          <CardContent className="px-0 pb-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>File</TableHead>
                    <TableHead className="w-[130px]">Type</TableHead>
                    <TableHead className="w-[180px]">Project</TableHead>
                    <TableHead className="w-[90px]">Version</TableHead>
                    <TableHead className="w-[120px]">Updated</TableHead>
                    <TableHead className="w-[220px] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {files.map((file) => {
                    const viewable = !!file.current_version && isViewable3D(file.current_version.file_name);
                    return (
                      <TableRow key={file.id}>
                        <TableCell>
                          <div className="flex min-w-0 items-center gap-2">
                            <FileIcon category={file.category} className="h-4 w-4 shrink-0 text-muted-foreground" />
                            <div className="min-w-0">
                              <div className="truncate text-sm font-medium text-foreground">{file.name}</div>
                              {file.description && (
                                <div className="truncate text-xs text-muted-foreground">{file.description}</div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {ENG_FILE_CATEGORY_LABELS[file.category as EngFileCategory] ?? file.category}
                        </TableCell>
                        <TableCell>
                          {file.project ? (
                            <Link to={`/engineering/${file.project.id}?tab=files`} className="block min-w-0 hover:underline">
                              <span className="font-mono text-xs text-muted-foreground">{file.project.code}</span>
                              <span className="block truncate text-sm">{file.project.name}</span>
                            </Link>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          v{file.current_version?.version_no ?? file.version_count}
                          <span className="block text-xs text-muted-foreground">{formatBytes(file.current_version?.size_bytes)}</span>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">{formatRelative(file.updated_at)}</TableCell>
                        <TableCell className="text-right">
                          <div className="inline-flex items-center gap-1">
                            {viewable && file.current_version && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  setViewer({
                                    storagePath: file.current_version!.storage_path,
                                    fileName: file.current_version!.file_name,
                                    title: file.name,
                                    subtitle: `v${file.current_version!.version_no}`,
                                  })
                                }
                              >
                                <Box className="h-4 w-4" />
                                View in 3D
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => handleDownload(file)} disabled={download.isPending}>
                              <Download className="h-4 w-4" />
                              Download
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {viewer && <ModelViewerDialog target={viewer} onClose={() => setViewer(null)} />}
    </div>
  );
}

/**
 * Compact "this part was engineered in…" card for the item Overview.
 * Renders nothing when the item has no engineering links so the
 * overview stays quiet for off-the-shelf parts.
 */
export function ItemEngineeringQuickLink({ itemId, className }: { itemId: string; className?: string }) {
  const { data } = useEngLinksForItem(itemId);
  const projects = (data?.projects ?? []).filter((l) => l.project);
  const fileCount = data?.files.length ?? 0;
  if (projects.length === 0 && fileCount === 0) return null;

  const primary =
    projects.find((l) => l.relationship === 'designs' && !l.project!.archived_at) ??
    projects.find((l) => !l.project!.archived_at) ??
    projects[0];

  return (
    <Card className={cn('border-info-soft/70', className)}>
      <CardContent className="flex flex-col gap-3 pt-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-info-soft/40 text-info">
            <DraftingCompass className="h-5 w-5" strokeWidth={1.6} />
          </span>
          <div className="min-w-0">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Engineering</p>
            {primary ? (
              <p className="truncate text-sm text-foreground">
                <span className="font-mono text-xs text-muted-foreground">{primary.project!.code}</span>{' '}
                <span className="font-medium">{primary.project!.name}</span>
                <EngStatusBadge status={primary.project!.status} className="ml-2 align-middle" />
                {projects.length > 1 && (
                  <span className="ml-2 text-xs text-muted-foreground">+{projects.length - 1} more</span>
                )}
              </p>
            ) : (
              <p className="text-sm text-foreground">
                {fileCount} engineering file{fileCount === 1 ? '' : 's'} pinned to this item
              </p>
            )}
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          {primary && (
            <Button size="sm" asChild>
              <Link to={`/engineering/${primary.project!.id}?tab=files`}>
                <Files className="h-4 w-4" />
                Engineering files
              </Link>
            </Button>
          )}
          <Button size="sm" variant="outline" asChild>
            <Link to={`/catalog/items/${itemId}?tab=engineering`}>Details</Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
